/**
 * Device detection utility for determining if the user is on an Android device
 * Uses user-agent detection as primary method with device fingerprinting as backup
 * Supports both Android phones and tablets across all browsers
 */

/**
 * Detects if the current device is Android based on user agent
 * Works for all Android devices (phones and tablets) and all browsers
 * Includes detection for Android desktop mode on tablets
 */
export const isAndroidDevice = (): boolean => {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

  // Check for Android in user agent (case insensitive)
  // This pattern appears in all Android devices regardless of browser
  const hasAndroid = /android/i.test(userAgent);

  // Additional patterns to catch Android devices:
  // - "Mobile" typically appears in phone user agents
  // - Tablets usually have "Android" without "Mobile"
  // - Some browsers may have different patterns
  const hasAndroidMobile = /android.*mobile/i.test(userAgent);
  const hasAndroidTablet = /android/i.test(userAgent) && !/mobile/i.test(userAgent);

  // Desktop mode detection: Android tablets in desktop mode show "Linux" in user agent
  const hasLinux = /linux/i.test(userAgent);

  // Return true if any Android pattern is detected
  return hasAndroid || hasAndroidMobile || hasAndroidTablet || hasLinux;
};

/**
 * Performs basic device fingerprinting as a backup detection method
 * Checks for touch-capable devices that aren't iOS
 * Browser-agnostic approach
 */
export const isAndroidFingerprint = (): boolean => {
  // Must have touch support
  const hasTouchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  if (!hasTouchSupport) {
    return false;
  }

  const userAgent = navigator.userAgent;

  // Exclude iOS devices
  const isAppleDevice = /iPhone|iPad|iPod|Macintosh/i.test(userAgent);
  if (isAppleDevice) {
    return false;
  }

  // Check for touch-capable device characteristics
  const hasTouchPointer = window.matchMedia('(hover: none) and (pointer: coarse)').matches;

  // At this point: has touch, not iOS, and has touch pointer characteristics
  // This is likely an Android device
  return hasTouchPointer;
};

/**
 * Combined detection: Returns true if device is detected as Android
 * by either user-agent or fingerprinting
 */
export const detectAndroidDevice = (): boolean => {
  return isAndroidDevice() || isAndroidFingerprint();
};
